import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component } from "preact";
/** @import Core from "~/app/lib/core/core.js"; */

import { apiHost, ApiError } from "~/lib/api.js";
import { setSession } from "~/lib/session.js";
import { generateSSONonce, storeSSONonce } from "~/lib/sso.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import PublicLayout, { FooterPrompt } from "./PublicLayout.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./SsoLogin.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./SsoLogin.module.css", import.meta.url);
const css = adopt(sheet);
class SsoLoginError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_request: "Invalid SSO request. Please try again.",
        invalid_state: "SSO session expired. Please try again.",
        server_error: "An error occurred. Please try again later.",
        sso_failed: "SSO authentication failed. Please try again.",
        invalid_email: "Invalid email address from SSO provider.",
        org_mismatch: "Your identity provider does not belong to this organization.",
        user_not_found: "Your account has not been provisioned. Please contact your IT administrator.",
        sso_timeout: "SSO login timed out. Please try again.",
        sso_cancelled: "SSO login was cancelled.",
        sso_not_configured: "SSO is not configured for this email domain."
      }
    };
  }
}

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {Core} [core]
 */
export default class SsoLogin extends Component {
  ssoEmail = signal(history.state?.email ?? "");
  loginWithSSO = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const email = this.ssoEmail.value.trim();
    if (!email) return;

    // Use native loopback flow when running in native app
    if (this.props.core?.native) {
      const result = await this.props.core.ssoLogin(email);
      if (result.error) {
        throw new ApiError(-1, [{
          code: result.error,
          message: "An error occurred during sign in."
        }]);
      }
      if (result.token) {
        setSession({
          token: result.token
        });
      }
    } else {
      const nonce = generateSSONonce();
      storeSSONonce(nonce);
      const redirectUri = `${window.location.origin}/sso/callback`;
      const params = new URLSearchParams({
        email,
        redirect_uri: redirectUri,
        nonce
      });
      window.location.href = `${apiHost.value}/v2/auth/sso/login?${params}`;
    }
  });
  render() {
    const ssoError = this.loginWithSSO.error && SsoLoginError.from(this.loginWithSSO.error);
    return __h(PublicLayout, {
      title: "Sign in with your company",
      onSubmit: this.loginWithSSO.invoke
    }, __h(Field, {
      "for": "sso-email",
      label: "Work email"
    }, __h("input", {
      "class": input.input,
      id: "sso-email",
      type: "email",
      value: this.ssoEmail.value,
      onInput: /** @param {Event & { target: HTMLInputElement }} e */e => {
        this.ssoEmail.value = e.target.value;
      },
      required: true
    })), __h("p", {
      "class": css.error
    }, ssoError?.message), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.loginWithSSO.loading
    }, "Continue", this.props.core?.native ? " in browser" : ""), __h(FooterPrompt, {}, __h("a", {
      href: "/login/"
    }, "Back to login")));
  }
}